import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchItems, postItem } from '../support/queries';

export type ResourceFeedbackRecord = {
  id: string | number;
  related_type?: string;
  related_id?: string;
  json?: any;
  se_phase?: string;
  created_at?: string;
  last_updated?: string;
  updated_by?: string;
};

export type SubmitResourceFeedbackInput = {
  resourceId: string;
  userEmail: string;
  details: string;
  entityId?: string;
  entityName?: string;
  bucket?: string;
  sourceType?: string;
  productId?: string | number;
  productName?: string;
};

type UseResourceFeedbackArgs = {
  resourceId?: string;
  enabled?: boolean;
};

export function useResourceFeedback({
                                      resourceId,
                                      enabled = true,
                                    }: UseResourceFeedbackArgs) {
  const [feedback, setFeedback] = useState<ResourceFeedbackRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const loadFeedback = useCallback(async () => {
    if (!enabled || !resourceId) {
      setFeedback([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const result = await fetchItems(
        'db/feedback',
        {
          related_type: 'resource',
          related_id: resourceId,
        },
        {
          includePageSize: false,
        }
      );

      const items = Array.isArray(result) ? result : [];
      setFeedback(items);
    } catch (err) {
      setError(err);
      setFeedback([]);
    } finally {
      setIsLoading(false);
    }
  }, [enabled, resourceId]);

  useEffect(() => {
    void loadFeedback();
  }, [loadFeedback]);

  const submitFeedback = useCallback(
    async ({
             resourceId,
             userEmail,
             details,
             entityId,
             entityName,
             bucket,
             sourceType,
             productId,
             productName,
           }: SubmitResourceFeedbackInput) => {
      if (!resourceId) {
        throw new Error('Missing resourceId for feedback submission.');
      }

      try {
        setIsSubmitting(true);
        setError(null);

        const feedbackJson = {
          type: 'resource_content_feedback',
          entityId: entityId || '',
          entityName: entityName || '',
          bucket: bucket || '',
          sourceType: sourceType || 'resource',
          sourceId: resourceId,
          details,
          productId: productId || '',
          productName: productName || '',
        };

        await postItem('db/feedback', {
          related_type: 'resource',
          related_id: resourceId,
          user: userEmail,
          se_phase: 'present',
          json: feedbackJson,
        });

        await loadFeedback();
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setIsSubmitting(false);
      }
    },
    [loadFeedback]
  );

  const isFlagged = useMemo(() => feedback.length > 0, [feedback]);

  const latestFeedback = useMemo(() => {
    if (!feedback.length) return null;

    return [...feedback].sort((a, b) => {
      const aTime = new Date(a.last_updated || a.created_at || 0).getTime();
      const bTime = new Date(b.last_updated || b.created_at || 0).getTime();
      return bTime - aTime;
    })[0];
  }, [feedback]);

  return {
    feedback,
    latestFeedback,
    isFlagged,
    isLoading,
    isSubmitting,
    error,
    reload: loadFeedback,
    submitFeedback,
  };
}